import { useMemo } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import FiltersPanel from "../components/log-monitor/FiltersPanel";
import SummaryCards from "../components/log-monitor/SummaryCards";
import LogsTable from "../components/log-monitor/LogsTable";
import ErrorCodeAccordions from "../components/log-monitor/ErrorCodeAccordions";
import useLogs from "../components/log-monitor/useLogs";
import { chooseBucketSize, distinctMessages, distinctTags } from "../components/log-monitor/formatters";

export default function LogMonitor() {
  const { data, loading, loadingMore, hasMore, error, load, loadMore } = useLogs();

  const bucketMinutes = useMemo(() => {
    if (!data?.meta?.window) return 15;
    const { from, until } = data.meta.window;
    return chooseBucketSize(
      from?.slice(0, 10),
      from?.slice(11, 16),
      until?.slice(0, 10),
      until?.slice(11, 16)
    );
  }, [data]);

  // Distinct message labels from the response, offered as suggestions in the Exclude box.
  const messageOptions = useMemo(() => distinctMessages(data?.logs || []), [data]);

  // Distinct tags from the response, offered as suggestions in the Tags box.
  const tagOptions = useMemo(() => distinctTags(data?.logs || []), [data]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box className="headingBtnWrap">
        <Typography variant="h1">Log Monitor</Typography>
      </Box>

      <Box sx={{ mt: "20px" }}>
        <FiltersPanel
          onFetch={load}
          loading={loading}
          includeOptions={messageOptions}
          excludeOptions={messageOptions}
          tagOptions={tagOptions}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data && (
          <Box>
            <SummaryCards summary={data.summary} />
            <LogsTable
              logs={data.logs || []}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMore}
            />
            <ErrorCodeAccordions logs={data.logs || []} bucketMinutes={bucketMinutes} />
          </Box>
        )}

        {!data && !loading && !error && (
          <Typography
            color="text.secondary"
            textAlign="center"
            sx={{ mt: 6, fontSize: "0.85rem" }}
          >
            Set filters and click Fetch to load logs.
          </Typography>
        )}
      </Box>
    </LocalizationProvider>
  );
}
